import {
  Alert, Input, Space, Tag, Typography,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { crearTraductor } from 'utils/traductorGraficas';

const { Text } = Typography;

const COLOR_POR_CATEGORIA = {
  'Qué medir': 'blue',
  'Agrupar por': 'green',
  Filtro: 'gold',
  Formato: 'purple',
  Periodo: 'purple',
};

/**
 * Cuadro "Describe la gráfica con tus palabras": traduce la frase con
 * `utils/traductorGraficas` (diccionario, sin IA) y entrega el resultado a
 * `onAplicar`, que llena el formulario. El formulario se sigue pudiendo editar
 * a mano después.
 */
const DescribirGraficaConPalabras = ({ catalogo, onAplicar }) => {
  const traducir = useMemo(() => crearTraductor(catalogo), [catalogo]);
  const [resultado, setResultado] = useState(null);
  const [notasExtra, setNotasExtra] = useState([]);
  const [aplicando, setAplicando] = useState(false);

  const alBuscar = async (frase) => {
    if (!frase || !frase.trim()) {
      setResultado(null);
      return;
    }
    const r = traducir(frase);
    setResultado(r);
    setNotasExtra([]);
    if (!r.ok) return;
    try {
      setAplicando(true);
      setNotasExtra((await onAplicar(r)) || []);
    } finally {
      setAplicando(false);
    }
  };

  const mensajes = [];
  if (resultado) {
    if (resultado.correcciones.length) {
      mensajes.push(['info', `Corregí la escritura: ${resultado.correcciones
        .map((c) => `«${c.de}» → «${c.a}»`).join(', ')}.`]);
    }
    resultado.errores.forEach((m) => mensajes.push(['error', m]));
    resultado.avisos.forEach((m) => mensajes.push(['warning', m]));
    resultado.notas.forEach((m) => mensajes.push(['info', m]));
    if (resultado.ok && resultado.noEntendi.length) {
      mensajes.push(['warning', `No reconocí: ${resultado.noEntendi.join(', ')}. Revisa que el formulario `
        + 'diga lo que querías.']);
    }
    notasExtra.forEach(([tipo, m]) => mensajes.push([tipo, m]));
    if (resultado.ok) {
      mensajes.push(['success', `Llené el formulario con la tabla «${resultado.config.tabla}». `
        + 'Revísalo y cambia lo que haga falta antes de guardar.']);
    }
  }

  return (
    <div style={{
      background: '#f6f8fb', border: '1px solid #e1e6ee', borderRadius: 8, padding: 12, marginBottom: 16,
    }}
    >
      <Text strong>Describe la gráfica con tus palabras (opcional)</Text>
      <Input.Search
        style={{ marginTop: 6 }}
        placeholder="Ej. cuántos empleados por dependencia, solo sindicalizados"
        enterButton="Llenar"
        allowClear
        loading={aplicando}
        onSearch={alBuscar}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        Usa «por …» para decir cómo agrupar (por dependencia, por mes, por rango de sueldo).
        No importan los acentos.
      </Text>
      {resultado && resultado.entendido.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {resultado.entendido.map((t) => (
            <Tag key={`${t.categoria}-${t.texto}`} color={COLOR_POR_CATEGORIA[t.categoria] || 'default'}>
              {t.texto}
              {' → '}
              {t.significado}
            </Tag>
          ))}
        </div>
      )}
      {mensajes.length > 0 && (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={4}>
          {mensajes.map(([tipo, m]) => <Alert key={m} type={tipo} message={m} showIcon />)}
        </Space>
      )}
    </div>
  );
};

DescribirGraficaConPalabras.propTypes = {
  catalogo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onAplicar: PropTypes.func.isRequired,
};

export default DescribirGraficaConPalabras;
